import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ManageAppAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymId: string;
  gymName: string;
  currentAppName?: string | null;
  onSuccess?: () => void;
}

const APP_COLORS: Record<string, string> = {
  StriFit: "bg-purple-500/20 text-purple-700 dark:text-purple-300",
  VB20TN: "bg-blue-500/20 text-blue-700 dark:text-blue-300",
  MPILATES: "bg-pink-500/20 text-pink-700 dark:text-pink-300",
  CROSSFIT_ELITE: "bg-orange-500/20 text-orange-700 dark:text-orange-300",
  YOGA_STUDIO: "bg-green-500/20 text-green-700 dark:text-green-300",
  BOXING_GYM: "bg-red-500/20 text-red-700 dark:text-red-300",
};

export function ManageAppAssignmentDialog({
  open,
  onOpenChange,
  gymId,
  gymName,
  currentAppName,
  onSuccess,
}: ManageAppAssignmentDialogProps) {
  const { t } = useTranslation("superadmin2");
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [availableApps, setAvailableApps] = useState<string[]>([]);
  const [selectedApp, setSelectedApp] = useState<string>(currentAppName || "");

  useEffect(() => {
    if (open) {
      fetchAvailableApps();
      setSelectedApp(currentAppName || "");
    }
  }, [open, currentAppName]);

  const fetchAvailableApps = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-app-assignment", {
        body: { action: "list_available_apps" },
      });

      if (error) throw error;

      if (data.success) {
        setAvailableApps(data.apps);
      } else {
        throw new Error(data.error || "Failed to fetch available apps");
      }
    } catch (error) {
      console.error("Error fetching available apps:", error);
      toast({
        title: t("manageAppAssignment.fetchAppsErrorTitle"),
        description: t("manageAppAssignment.fetchAppsErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!selectedApp) {
      toast({
        title: t("manageAppAssignment.fetchAppsErrorTitle"),
        description: t("manageAppAssignment.selectAppErrorDesc"),
        variant: "destructive",
      });
      return;
    }

    if (selectedApp === currentAppName) {
      onOpenChange(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-app-assignment", {
        body: {
          action: "update_assignment",
          gym_id: gymId,
          app_name: selectedApp,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t("manageAppAssignment.successTitle"),
          description: t("manageAppAssignment.successDesc", { gymName, appName: selectedApp }),
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        throw new Error(data.error || "Failed to update assignment");
      }
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast({
        title: t("manageAppAssignment.fetchAppsErrorTitle"),
        description: t("manageAppAssignment.updateErrorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const content = (
    <>
      <Alert className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {t("manageAppAssignment.warning")}
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">{t("manageAppAssignment.gymLabel")}</label>
          <p className="text-sm text-muted-foreground">{gymName}</p>
        </div>

        {currentAppName && (
          <div>
            <label className="text-sm font-medium mb-2 block">{t("manageAppAssignment.currentAppLabel")}</label>
            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${APP_COLORS[currentAppName] || "bg-muted text-muted-foreground"}`}>
              {currentAppName}
            </span>
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-2 block">{t("manageAppAssignment.newAppLabel")}</label>
          <Select value={selectedApp} onValueChange={setSelectedApp}>
            <SelectTrigger>
              <SelectValue placeholder={t("manageAppAssignment.selectAppPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {availableApps.map((app) => (
                <SelectItem key={app} value={app}>
                  {app}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );

  const footer = (
    <>
      <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
        {t("manageAppAssignment.cancel")}
      </Button>
      <Button onClick={handleSave} disabled={isLoading || !selectedApp}>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t("manageAppAssignment.save")}
      </Button>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t("manageAppAssignment.title")}</DrawerTitle>
            <DrawerDescription>
              {t("manageAppAssignment.description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{content}</div>
          <DrawerFooter className="flex flex-row gap-2">{footer}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("manageAppAssignment.title")}</DialogTitle>
          <DialogDescription>
            {t("manageAppAssignment.description")}
          </DialogDescription>
        </DialogHeader>
        {content}
        <DialogFooter>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
